import React, { useState, useMemo, useEffect } from 'react';
import { Box, Drawer, useTheme, alpha } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { LAYOUT } from '../../constants/layout';
import Header from './Header/Header';
import MenuItems from './Sidebar/MenuItems';
import BottomMenu from './Sidebar/BottomMenu';
import MenuHeader from './Sidebar/MenuHeader';
import OnboardingTour from './OnboardingTour';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { fetchPaymentSettings } from '../../store/slices/paymentSlice';
import { setSelectedOrganizationId } from '../../store/slices/organizationContextSlice';

export default function DashboardLayout() {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { role } = useAuth();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  // Sync organizationContext.selectedOrganizationId with organizations.selectedOrganisation
  // This ensures hooks like useBookingDetail can access the organization ID consistently
  useEffect(() => {
    if (selectedOrganization?.id) {
      dispatch(setSelectedOrganizationId(selectedOrganization.id));
    }
  }, [selectedOrganization?.id, dispatch]);

  // Fetch payment settings when organization changes and booking is enabled
  useEffect(() => {
    if (selectedOrganization?.id && selectedOrganization?.isBookingEnabled) {
      dispatch(fetchPaymentSettings(selectedOrganization.id));
    }
  }, [selectedOrganization?.id, selectedOrganization?.isBookingEnabled, dispatch]);

  const { showTrialBanner, headerOffset, toolbarHeight } = useMemo(() => {
    const isTrial = selectedOrganization?.isTrailEnabled;
    const trialEndDate = new Date(selectedOrganization?.subscriptionEndDate || '');
    const today = new Date();
    const diffTime = trialEndDate?.getTime() - today.getTime();
    const trialDaysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const displayBanner = isTrial && trialDaysRemaining <= 7 && role !== AUTH_CONSTANTS.ROLES.USER;
    const trialBannerHeight = displayBanner ? 15 : 0;
    const computedToolbarHeight = displayBanner
      ? LAYOUT.TOOLBAR_HEIGHT - 15
      : LAYOUT.TOOLBAR_HEIGHT;

    return {
      showTrialBanner: displayBanner,
      headerOffset: computedToolbarHeight + trialBannerHeight,
      toolbarHeight: computedToolbarHeight,
    };
  }, [role, selectedOrganization]);
  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
      }}
    >
      {role !== AUTH_CONSTANTS.ROLES.USER && <OnboardingTour />}

      {/* Fixed Header Area */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          display: 'flex',
        }}
      >
        <MenuHeader
          isDrawerOpen={isDrawerOpen}
          onMenuClick={handleDrawerToggle}
          toolbarHeight={toolbarHeight}
        />
        <Header isDrawerOpen={isDrawerOpen} />
      </Box>

      {/* Navigation Drawer - Google Style */}
      <Drawer
        variant="permanent"
        sx={{
          width: isDrawerOpen ? LAYOUT.DRAWER_WIDTH : LAYOUT.COLLAPSED_DRAWER_WIDTH,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          border: 'none',
          '& .MuiDrawer-paper': {
            width: isDrawerOpen ? LAYOUT.DRAWER_WIDTH : LAYOUT.COLLAPSED_DRAWER_WIDTH,
            transition: theme.transitions.create(['width'], {
              easing: 'cubic-bezier(0.2, 0, 0, 1)',
              duration: 280,
            }),
            mt: `${headerOffset}px`,
            overflowX: 'hidden',
            backgroundColor: theme.palette.background.paper,
            border: 'none !important',
            borderRight: 'none !important',
            boxShadow: 'none !important',
            '&::after': {
              display: 'none',
            },
            '&::before': {
              display: 'none',
            },
          },
        }}
        open={isDrawerOpen}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: `calc(100% - ${LAYOUT.TOOLBAR_HEIGHT}px)`,
            overflow: 'hidden',
            px: isDrawerOpen ? 1.5 : 1,
            py: 2,
            position: 'relative',
          }}
        >
          {/* Scrollable Menu Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              marginBottom: '60px',
              // Custom scrollbar styling
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.text.secondary, 0.2),
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: alpha(theme.palette.text.secondary, 0.3),
              },
            }}
          >
            <MenuItems isDrawerOpen={isDrawerOpen} />
          </Box>
          <BottomMenu isDrawerOpen={isDrawerOpen} />
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: {
            sm: `calc(100% - ${isDrawerOpen ? LAYOUT.DRAWER_WIDTH : LAYOUT.COLLAPSED_DRAWER_WIDTH}px)`,
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: 'cubic-bezier(0.2, 0, 0, 1)',
            duration: 280,
          }),
          minHeight: `calc(100vh - ${headerOffset}px)`,
          mt: `${headerOffset}px`,
          // Add subtle background pattern for depth
          background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
